import { ContentBlockTypes, UsefulLinkColors, UsefulLinkIcons } from "@prisma/client";
import { z } from "zod";

//scheme for adding url/tag of yt channel
export const addYoutuberUrl_scheme = z.string().trim()
    .min(4, "קישור או טאג קצרים מידי")
    .and(z.string().trim()
        .url("קישור לא תקין")
        .includes("youtube.com/", { "message": "אפשר רק קישור לערוץ יוטיוב" })
        .or(z.string().trim()
            .startsWith("@", "טאג לא תקין")))

//scheme for seasons
export const season_scheme = z.object({
    name: z.string().trim().min(5, "קצר מידי, חייב לפחות אורך 5 תווים"),
    shortName: z.string({ "invalid_type_error": "דגדג" }).trim()
        .min(3, "קצר מידי, חייב לפחות אורך 3 תווים")
        .max(10, "שם ארוך מידי - זה אמור להיות קצר וקליט")
        .regex(/^[A-Za-z\d]*$/, "רק תווים באנגלית ומספרים"),
    mapDownloadLink: z.string().trim().url("קישור לא תקין").startsWith("https://", "חייב להתחיל ב https://").optional().or(z.literal("")).nullable().default(null)
})

//scheme for adding video
export const addVideo_scheme = z.string().trim()
    .min(6, "קישור או מזהה קצרים מידי")
    .and(z.string().trim()
        .url("קישור לא תקין")
        .startsWith("https://", "חייב להתחיל ב https://")
        .includes("youtube.com/watch?v=", { "message": "אפשר רק קישור לסרטון יוטיוב => https://www.youtube.com/watch?v={ID}" })
        .or(z.string().trim()
            .min(5, "מזהה קצר מידי")
            .max(12, "מזהה ארוך מידי")));

//scheme for UsefulLinks / panel links
export const UsefulLink_scheme = z.object({
    id: z.number().optional(),
    title: z.string().trim().min(3, "כותרת קצרה מידי"),
    icon: z.nativeEnum(UsefulLinkIcons, { "message": "חובה לבחור סמל" }),
    color: z.nativeEnum(UsefulLinkColors, { "message": "חובה לבחור צבע" }),
    description: z.string().trim().min(5, "תיאור קצר מידי").optional().or(z.literal("")).nullable().default(null),
    interactiveValue: z.string().trim().min(3, "כתובת / העתק-הדבק קצר מידי"),
    asCopyPaste: z.boolean().default(false),
})


export const projects_scheme = z.object({
    id: z.number().optional(),
    buildTitle: z.string().trim().min(3, "כותרת קצרה מידי").max(30, "כותרת ארוכה מידי"),
    seasonShortName: z.string().trim().min(1, "יש לבחור את העונה שהפרויקט נבנה בה"),
    users: z.array(z.string(), { "message": "לא נבחר שחקן" }).min(1, "יש לבחור לפחות משתמש אחד שקשור לפרויקט"),
    file: z.object({ size: z.number().min(1, "יש להעלות תמונה").max(1024 * 1024 * 7, "תמונה גדולה מידי, אפשר עד 7 מגהבייט") }).optional()
})

export const videoSearchScheme = z.object({
    /**search value to llok for*/
    search: z.string({ "required_error": "יש לציין ערך לחיפוש" }).trim().min(3, "מינימום 3 תווים לחיפוש").max(150, "טקסט לחיפוש ארוך מידי"),
    /** pagination page indexed at 0 */
    page: z.number().min(0, "אי אפשר לציין דף שלילי").max(10, "הגעת לכמות מקסימלית של תוצאות לחיפוש").optional(),
    /** possible search by youtuberID */
    ytid: z.string().trim().optional(),
    /** possible search by season */
    seShortName: z.string().trim().optional(),
})

/** google form scheme */
export const googleFormsScheme = z.object({
    /** possible id incase of editing a form  */
    id: z.number({ "required_error": "לא סופק מזהה טופס" }).optional(),
    /** the page title of the form page */
    title: z.string({ "required_error": "חייב לספק כותרת לטופס" }).trim()
        .min(3, "קצר מידי, חייב לפחות 3 תווים")
        .max(30, "ארוך מידי - זה אמור להיות קצר וקליט"),
    /** embed link url */
    googleFormsEmbedUrl: z.string({ "required_error": "חייב לספק כתובת טופס" }).trim()
        .url("קישור לא תקין")
        .startsWith("https://", "חייב להתחיל ב https://"),
    /** url param that will host the form page */
    urlParam: z.string().trim().min(3, "קצר מידי, חייב לפחות 3 תווים")
        .max(15, "ארוך מידי - זה אמור להיות קצר וקליט"),
    /** OPTIONAL a date to schedule when the form should be open to the public */
    openAt: z.date({
        coerce: true, //parse
        required_error: "חייב לציין תאריך",
        invalid_type_error: "תאריך לא תקין",
    }).default(new Date()),
    countDown: z.preprocess(value => value === 'on', z.boolean()).default(false)
})

/** qna form scheme */
export const qnaScheme = z.object({
    /** possible id incase of editing a QnA  */
    id: z.preprocess(value => Number(value), z.number(({ "invalid_type_error": "מזהה שו'ת לא תקין" }))).optional(),
    /** the Q */
    q: z.string({ "required_error": "חייב לספק כותרת לטופס" }).trim()
        .min(3, "קצר מידי, חייב לפחות 3 תווים")
        .max(60, "ארוך מידי"),
    a: z.string({ "required_error": "חייב לספק תשובה לשאלה" }).trim()
        .min(100, "קצר מידי, יש להכניס עוד תוכן")
        .max(3000, "זה כנראה יותר מידי תוכן לא?"),
    icon: z.nativeEnum(UsefulLinkIcons, { "message": "חובה לבחור סמל", "required_error": "חובה לבחור סמל" }),
})

/** api scheme for PlayersSmpStats */
export const PlayersSmpStats_API_Scheme = z.record(
    //key
    z.string().uuid("Invalid minecraft uuid"),
    //value
    z.object({
        name: z.string().min(2, "Player name must be at least 2 letter long."),
        time: z.number()
            .nonnegative("Playtime cannot be negative.")
            .default(0),
        afktime: z.number()
            .nonnegative("Afktime cannot be negative.")
            .default(0),
        dirt: z.number()
            .nonnegative("Dirt placed cannot be negative.")
            .default(0),
        d: z.number()
            .nonnegative("Deaths cannot be negative.")
            .default(0),
        k: z.number()
            .nonnegative("Kills cannot be negative.")
            .default(0),
        quit: z.number()
            .nonnegative("Game quits cannot be negative.")
            .default(0),
        bats: z.number()
            .nonnegative("Bats killed cannot be negative.")
            .default(0),
    })
);

/** contentBlock form scheme */
export const contentBlockScheme = z.object({
    /** tiptap content */
    content: z.string({ "required_error": "חייב לספק תשובה לשאלה" }).trim()
        .min(100, "קצר מידי, יש להכניס עוד תוכן")
        .max(4000, "זה כנראה יותר מידי תוכן לא?"),
    /** relation block name */
    blockName: z.nativeEnum(ContentBlockTypes, { "message": "חובה לבחור שיוך", "required_error": "חובה לבחור שיוך" }),
})


export const giftsScheme = z.object({
    id: z.string({ "required_error": "לא סופק מזהה חלוקת פרסים" }).min(3, "מזהה קצר מידי").max(30, "מזהה ארוך מידי").optional(),
    description: z.string({ "required_error": "חייב לספק תיאור לחלוקת פרסים" }).trim()
        .min(100, "קצר מידי, יש להכניס עוד תוכן")
        .max(4000, "זה כנראה יותר מידי תוכן, לא?"),
    readableName: z.string().trim().min(3, "קצר מידי").max(40, "ארוך מידי"),
    maxParticipants: z.preprocess(value => Number(value || 100),
        z.number(({ "invalid_type_error": "מספר לא תקין" })).min(1, "מינימום 1").max(10000, "מקסימום 10,000")
    )

})

export const giftJoinScheme = z.object({
    giftID: giftsScheme.shape.id.refine(val => val !== undefined, {
        message: "לא סופק מזהה חלוקת פרסים",
    }),
    phone: z.string().transform((val) => val.replace(/\D/g, "")).refine((val) => /^05\d{8}$/.test(val), "לא הוזן טלפון תקין").or(z.literal("")),
    email: z.string().email("לא הוזן אמייל תקין").or(z.literal(""))
})

export const srvRecordScheme = z.object({
    id: z.string({ "required_error": "לא סופק מזהה" }).min(1, "מזהה קצר מידי").max(100, "מזהה ארוך מידי").optional(),
    /** the port the srv record opens */
    port: z.preprocess(value => Number(value), z.number(({ "invalid_type_error": "פורט לא תקין" }))
        .min(1, "חייב להיות בין 10000 ל 65535")
        .max(65535, "חייב להיות בין 10000 ל 65535")),
    /** the subdomain that need to be used to login to the server */
    subdomain: z.string().trim().min(3, "חייב להיות לפחות 3 תווים").max(20, "חייב להיות עד 20 תווים").regex(/^[A-Za-z\d]*$/, "רק תווים באנגלית ומספרים"),
    /** readable comment in cloudflare's panel */
    comment: z.string().trim().min(5, "חייב להיות לפחות 5 תווים").max(100, "חייב להיות עד 100 תווים"),
});